import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.scss';
import Callback from './components/Callback';
import User from './components/User';
import UserEdit from './components/UserEdit';
import ResourceNav from './components/ResourceNav';
import Dashboard from './components/Dashboard';
import TenantsPage from './components/TenantsPage';
import EnvironmentsPage from './components/EnvironmentsPage';

import ConnectionsOverview from './components/ConnectionsOverview';
import GitHubConfiguration from './components/GitHubConfiguration';
import GitHubConfigurations from './components/GitHubConfigurations';
import GitHubAppManagement from './components/GitHubAppManagement';
import GitHubAppCallback from './components/GitHubAppCallback';
import AwsConnections from './components/AwsConnections';
import { ToastProvider } from './contexts/ToastContext';
import { ChatProvider } from './contexts/ChatContext';


const queryClient = new QueryClient();

// Set page title based on environment
const setPageTitle = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    const displayName = process.env.REACT_APP_APPLICATION_DISPLAY_NAME || 'Product Catalog';
    
    if (environment && environment !== 'production') {
        document.title = `${environment.toUpperCase()} - ${displayName}`;
    } else {
        document.title = displayName;
    }
};

const MobileMenuButton: React.FC = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isMobile && isMenuOpen) {
            const handleClickOutside = (event: MouseEvent) => {
                const target = event.target as Element;
                if (!target.closest('[data-menu="true"]')) {
                    setIsMenuOpen(false);
                }
            };
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [isMobile, isMenuOpen]);

    if (!isMobile) return null;

    return (
        <>
            <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                data-menu="true"
                className="mobile-menu-button"
            >
                ☰
            </button>
            {isMenuOpen && (
                <ResourceNav onTenantSelect={() => { }} isMobileOverlay={true} onClose={() => setIsMenuOpen(false)} />
            )}
        </>
    );
};

const Layout: React.FC<{ children: React.ReactNode; breadcrumb?: string; showColumns?: boolean }> = ({ children, breadcrumb, showColumns = false }) => {
    return (
        <div className="App">
            <MobileMenuButton />
            <nav className="layout__nav">
                <Link to="/" className="layout__nav-title">
                    <h1>{process.env.REACT_APP_APPLICATION_DISPLAY_NAME || 'Product Catalog'}</h1>
                </Link>
                <User />
            </nav>

            <div className="layout__breadcrumb">
                <Link to="/" className="layout__breadcrumb-link">Home</Link>
                {breadcrumb && ` / ${breadcrumb}`}
            </div>

            <main className="layout__main">
                {showColumns ? (
                    <ColumnarLayout>{children}</ColumnarLayout>
                ) : (
                    children
                )}
            </main>

            <footer className="layout__footer">
                <div>© Rainmaking.cloud {new Date().getFullYear()}</div>
                <div className="layout__footer-build">{process.env.REACT_APP_BUILD_DATE ? `Built on ${process.env.REACT_APP_BUILD_DATE}` : `Loaded at ${new Date().toLocaleString()}`}</div>
            </footer>
        </div>
    );
};

const ColumnarLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

    return (
        <div className="columnar-layout">
            {/* Column 1: Resource Navigation */}
            <ResourceNav onTenantSelect={setSelectedTenant} />

            {/* Column 2: Resource Details */}
            <div className={`columnar-layout__content ${selectedTenant ? 'columnar-layout__content--with-border' : ''}`}>
                {React.isValidElement(children) ? React.cloneElement(children, { onTenantSelect: setSelectedTenant } as any) : children}
            </div>

        </div>
    );
};



function App() {
    useEffect(() => {
        setPageTitle();
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <ToastProvider>
                <ChatProvider>
                    <Router>
                <Routes>

                    <Route path="/" element={
                        <Layout showColumns={true}>
                            <div>   </div>
                        </Layout>
                    } />

                    <Route path="/dashboard" element={
                        <Layout showColumns={true}>
                            <Dashboard />
                        </Layout>
                    } />

                    <Route path="/callback" element={<Callback />} />

                    <Route path="/tenants" element={
                        <Layout breadcrumb="Tenants" showColumns={true}>
                            <TenantsPage />
                        </Layout>
                    } />

                    <Route path="/tenants/:tenantId" element={
                        <Layout breadcrumb="Tenants" showColumns={true}>
                            <TenantsPage />
                        </Layout>
                    } />

                    <Route path="/tenants/:tenantId/environments" element={
                        <Layout breadcrumb="Tenants / Environments" showColumns={true}>
                            <EnvironmentsPage />
                        </Layout>
                    } />

                    <Route path="/tenants/:tenantId/environments/:environmentId" element={
                        <Layout breadcrumb="Tenants / Environments" showColumns={true}>
                            <EnvironmentsPage />
                        </Layout>
                    } />

                    <Route path="/tenants/:tenantId/environments/:environmentId/summary" element={
                        <Layout breadcrumb="Tenants / Environments" showColumns={true}>
                            <EnvironmentsPage />
                        </Layout>
                    } />

                    <Route path="/tenants/:tenantId/environments/:environmentId/edit" element={
                        <Layout breadcrumb="Tenants / Environments" showColumns={true}>
                            <EnvironmentsPage />
                        </Layout>
                    } />

                    <Route path="/tenants/:tenantId/environments/:environmentId/chat" element={
                        <Layout breadcrumb="Tenants / Environments" showColumns={true}>
                            <EnvironmentsPage />
                        </Layout>
                    } />

                    <Route path="/edit-user" element={
                        <Layout breadcrumb="Edit Profile">
                            <UserEdit />
                        </Layout>
                    } />

                    <Route path="/connections" element={
                        <Layout breadcrumb="Connections" showColumns={true}>
                            <ConnectionsOverview />
                        </Layout>
                    } />

                    <Route path="/connections/github" element={
                        <Layout breadcrumb="Connections / GitHub" showColumns={true}>
                            <GitHubConfigurations />
                        </Layout>
                    } />

                    <Route path="/connections/github/:connectionId" element={
                        <Layout breadcrumb="Connections / GitHub" showColumns={true}>
                            <GitHubConfigurations />
                        </Layout>
                    } />

                    <Route path="/connections/aws" element={
                        <Layout breadcrumb="Connections / AWS" showColumns={true}>
                            <AwsConnections />
                        </Layout>
                    } />

                    <Route path="/connections/aws/:connectionId" element={
                        <Layout breadcrumb="Connections / AWS" showColumns={true}>
                            <AwsConnections />
                        </Layout>
                    } />

                    <Route path="/configurations/connections/github" element={
                        <Layout breadcrumb="Connections / GitHub SSH/PAT" showColumns={true}>
                            <GitHubConfiguration />
                        </Layout>
                    } />

                    <Route path="/configurations/connections/github-app" element={
                        <Layout breadcrumb="Connections / GitHub App" showColumns={true}>
                            <GitHubAppManagement />
                        </Layout>
                    } />

                    <Route path="/integrations/github/callback/:tenantId" element={
                        <Layout breadcrumb="GitHub App Installation">
                            <GitHubAppCallback />
                        </Layout>
                    } />

                    <Route path="/integrations/github/callback" element={
                        <Layout breadcrumb="GitHub App Installation">
                            <GitHubAppCallback />
                        </Layout>
                    } />


                </Routes>
                    </Router>
                </ChatProvider>
            </ToastProvider>
        </QueryClientProvider>
    );
}

export default App;